/**
 * The design's deterministic generative art, ported verbatim from the
 * OpenDesign app mock (web/design/o1-launchpad-app.html): every specimen's
 * plate is drawn from its genome symbol, so the same neuron always renders
 * the same engraving in every browser.
 */

type Random = () => number;

/** The mock's own string-hash rng, kept byte-faithful so plates never change. */
function hashStr(seed: string): Random {
  let h = 1779033703 ^ seed.length;

  for (let at = 0; at < seed.length; at += 1) {
    h = Math.imul(h ^ seed.charCodeAt(at), 3432918353);
    h = (h << 13) | (h >>> 19);
  }

  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);

    return (h ^= h >>> 16) >>> 0;
  };
}

function rng(seed: string): Random {
  const next = hashStr(seed);
  let a = next();

  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const NS = "http://www.w3.org/2000/svg";

const FG = "oklch(92% 0.012 95)";

const AMB = "oklch(82% 0.135 82)";

const LN = "oklch(60% 0.02 195)";

function mk(tag: string, at: Record<string, string | number>): SVGElement {
  const element = document.createElementNS(NS, tag);

  for (const key of Object.keys(at)) element.setAttribute(key, String(at[key]));

  return element;
}

export function drawArt(svg: Element): void {
  const seed = svg.getAttribute("data-seed") ?? "obrain";
  const size = Number.parseFloat(svg.getAttribute("data-size") ?? "") || 120;
  const variant = Number.parseInt(svg.getAttribute("data-variant") ?? "0", 10);
  const R = rng(`genome:${seed}`);
  const c = size / 2;
  const S = size;

  svg.setAttribute("viewBox", `0 0 ${S} ${S}`);

  while (svg.firstChild !== null) svg.removeChild(svg.firstChild);

  const big = variant === 3;

  if (big) {
    svg.appendChild(
      mk("rect", {
        x: 8,
        y: 8,
        width: S - 16,
        height: S - 16,
        fill: "none",
        stroke: "oklch(45% 0.02 200 / 0.7)",
        "stroke-width": 1,
      }),
    );

    const corners = [
      [18, 18, 1, 1],
      [S - 18, 18, -1, 1],
      [18, S - 18, 1, -1],
      [S - 18, S - 18, -1, -1],
    ] as const;

    for (const p of corners) {
      svg.appendChild(
        mk("path", {
          d: `M${p[0]} ${p[1] + 10 * p[3]} L${p[0]} ${p[1]} L${p[0] + 10 * p[2]} ${p[1]}`,
          stroke: LN,
          fill: "none",
          "stroke-width": 1,
          opacity: 0.8,
        }),
      );
    }

    const crosses = [
      [S / 2, 14],
      [14, S / 2],
    ] as const;

    for (const p of crosses) {
      svg.appendChild(
        mk("path", {
          d: `M${p[0] - 5} ${p[1]} H${p[0] + 5} M${p[0]} ${p[1] - 5} V${p[1] + 5}`,
          stroke: LN,
          fill: "none",
          "stroke-width": 0.8,
          opacity: 0.55,
        }),
      );
    }
  } else {
    svg.appendChild(
      mk("circle", {
        cx: c,
        cy: c,
        r: c - 2,
        fill: "none",
        stroke: "oklch(40% 0.02 200 / 0.5)",
        "stroke-width": 1,
      }),
    );
  }

  const orbR = c * (big ? 0.84 : 0.8);
  const nDots = big ? 110 : 64;

  for (let i = 0; i < nDots; i += 1) {
    const angle = (i / nDots) * Math.PI * 2 + R() * 0.04;
    const rr = orbR * (0.97 + R() * 0.06);

    svg.appendChild(
      mk("circle", {
        cx: (c + Math.cos(angle) * rr).toFixed(1),
        cy: (c + Math.sin(angle) * rr).toFixed(1),
        r: (0.5 + R() * 0.7).toFixed(2),
        fill: FG,
        opacity: (0.12 + R() * 0.22).toFixed(2),
      }),
    );
  }

  const arcs = big ? 4 : 2 + (R() < 0.5 ? 1 : 0);

  for (let j = 0; j < arcs; j += 1) {
    const ar = c * (0.3 + R() * 0.55);

    svg.appendChild(
      mk("circle", {
        cx: c,
        cy: c,
        r: ar.toFixed(1),
        fill: "none",
        stroke: FG,
        "stroke-width": 0.7,
        opacity: (0.2 + R() * 0.25).toFixed(2),
        pathLength: 100,
        "stroke-dasharray": `${(8 + R() * 22).toFixed(0)} ${(10 + R() * 26).toFixed(0)}`,
        "stroke-dashoffset": (R() * 100).toFixed(0),
        "stroke-linecap": "round",
      }),
    );
  }

  const spokes = big ? 40 : 16 + ((R() * 14) | 0);

  for (let k = 0; k < spokes; k += 1) {
    const angle = (k / spokes) * Math.PI * 2 + (R() - 0.5) * 0.12;
    const r0 = c * (0.1 + R() * 0.22);
    const r1 = c * (0.5 + R() * 0.38);

    svg.appendChild(
      mk("line", {
        x1: (c + Math.cos(angle) * r0).toFixed(1),
        y1: (c + Math.sin(angle) * r0).toFixed(1),
        x2: (c + Math.cos(angle) * r1).toFixed(1),
        y2: (c + Math.sin(angle) * r1).toFixed(1),
        stroke: FG,
        "stroke-width": 0.55,
        opacity: (0.22 + R() * 0.3).toFixed(2),
      }),
    );
  }

  const nodes = (variant === 2 ? 16 : 8) + ((R() * 7) | 0) + (big ? 4 : 0);

  for (let m = 0; m < nodes; m += 1) {
    const a2 = R() * Math.PI * 2;
    const rr2 = c * (0.16 + R() * 0.66);
    const nx = c + Math.cos(a2) * rr2;
    const ny = c + Math.sin(a2) * rr2;
    const nr = (0.8 + R() * 1.7) * (big ? 1.5 : 1);

    if (m === 0) {
      svg.appendChild(
        mk("circle", {
          cx: nx.toFixed(1),
          cy: ny.toFixed(1),
          r: (nr * 4.5).toFixed(1),
          fill: AMB,
          opacity: 0.12,
        }),
      );
      svg.appendChild(
        mk("circle", {
          cx: nx.toFixed(1),
          cy: ny.toFixed(1),
          r: (nr * 2.2).toFixed(1),
          fill: AMB,
          opacity: 0.25,
        }),
      );
    }

    svg.appendChild(
      mk("circle", {
        cx: nx.toFixed(1),
        cy: ny.toFixed(1),
        r: nr.toFixed(2),
        fill: AMB,
        opacity: (0.55 + R() * 0.45).toFixed(2),
      }),
    );
  }

  if (big) {
    for (let hy = S * 0.86; hy < S * 0.955; hy += 3.2) {
      svg.appendChild(
        mk("line", {
          x1: S * 0.09,
          y1: hy.toFixed(1),
          x2: S * 0.91,
          y2: hy.toFixed(1),
          stroke: FG,
          "stroke-width": 0.5,
          opacity: 0.1,
        }),
      );
    }

    const t1 = mk("text", {
      x: S - 20,
      y: S - 20,
      "text-anchor": "end",
      "font-size": 11,
      "letter-spacing": 2,
      fill: FG,
      opacity: 0.55,
      "font-family": "ui-monospace, Menlo, monospace",
    });

    // The plate marks are baked into the deterministic specimen art - the
    // hatched token's image - so they stay pixel-identical in every language
    // and are deliberately exempt from i18n, like the Latin taxonomy.
    t1.textContent = "PL. XI";
    svg.appendChild(t1);

    const t2 = mk("text", {
      x: 20,
      y: S - 20,
      "font-size": 9.5,
      "letter-spacing": 1.6,
      fill: FG,
      opacity: 0.4,
      "font-family": "ui-monospace, Menlo, monospace",
    });

    t2.textContent = `GENOME ${seed}`;
    svg.appendChild(t2);
  }
}

/**
 * The same drawing as a standalone SVG document.
 *
 * This is the token's own image: the launch uploads a PNG rasterized from it, so
 * the logo a hatch carries is the plate the visitor reviewed. Nothing here reads
 * the page - the drawing sets its own colours - which is what makes it safe to
 * rasterize off-document.
 */
export function artSvg(seed: string, size: number, variant: number): string {
  const svg = mk("svg", {
    xmlns: NS,
    width: size,
    height: size,
    "data-seed": seed,
    "data-size": size,
    "data-variant": variant,
  });

  drawArt(svg);

  return new XMLSerializer().serializeToString(svg);
}

/** Draws every svg[data-seed] under root; dynamic cards call it after render. */
export function drawAllArt(root: ParentNode): void {
  const svgs = root.querySelectorAll<SVGElement>("svg[data-seed]");

  for (const svg of svgs) drawArt(svg);
}
